import {
  Flex,
  IconButton,
  IconArrowOpenUpLine,
  IconArrowOpenDownLine,
  IconTrashLine,
  Pill,
  ProgressBar,
  Text,
  View,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import RichTextEditor from "../../../components/RichTextArea/RichTextEditor";

/**
 * Resolve the hydrated content for an assessment-type agenda item.
 */
const renderAssessmentItem = (
  item,
  assessment,
  insightsTerms,
  canvasTerms,
  t,
  resolveTermStatuses
) => {
  const termStatuses = resolveTermStatuses(
    assessment,
    insightsTerms,
    canvasTerms
  );
  const totalTerms = termStatuses.length;
  const completedTerms = termStatuses.filter((ts) => ts.ended).length;

  return (
    <View as="div">
      <Flex alignItems="center" gap="small" wrap="wrap">
        <Flex.Item>
          <Text weight="bold">{item.item_name}</Text>
        </Flex.Item>
      </Flex>

      {termStatuses.length > 0 && (
        <View as="div" margin="small 0 0 0">
          <Flex wrap="wrap" gap="x-small">
            {termStatuses.map((ts, idx) => {
              let pillColor = "primary";
              let prefix = "";
              if (ts.ended) {
                pillColor = "success";
                prefix = "✓ ";
              } else if (ts.isCurrent) {
                pillColor = "info";
                prefix = "→ ";
              }
              return (
                <Flex.Item key={idx}>
                  <Pill color={pillColor}>
                    {prefix}
                    {ts.name}
                  </Pill>
                </Flex.Item>
              );
            })}
          </Flex>
        </View>
      )}

      {totalTerms > 0 && (
        <View as="div" margin="small 0 0 0">
          <ProgressBar
            size="x-small"
            screenReaderLabel={t(
              "assessmentPlanningDashboard.meetingAgenda.termsCollected",
              {
                percentage: Math.round((completedTerms / totalTerms) * 100),
                completed: completedTerms,
                total: totalTerms,
              }
            )}
            valueNow={completedTerms}
            valueMax={totalTerms}
            meterColor={completedTerms === totalTerms ? "success" : "info"}
          />
          <Text size="x-small" color="secondary">
            {t("assessmentPlanningDashboard.meetingAgenda.termsCollected", {
              percentage: Math.round((completedTerms / totalTerms) * 100),
              completed: completedTerms,
              total: totalTerms,
            })}
          </Text>
        </View>
      )}
    </View>
  );
};

/**
 * Resolve the hydrated content for a qualitative-measure-type agenda item.
 */
const renderQualitativeMeasureItem = (item, measure, insightsTerms, t) => {
  const status = measure.status || "not_started";
  const statusKeys = {
    not_started: "assessmentPlanningDashboard.meetingAgenda.statusNotStarted",
    in_progress: "assessmentPlanningDashboard.meetingAgenda.statusInProgress",
    submitted: "assessmentPlanningDashboard.meetingAgenda.statusSubmitted",
  };
  const statusColors = {
    not_started: "primary",
    in_progress: "warning",
    submitted: "success",
  };

  let dueTermName = "";
  if (measure.insights_term_id && insightsTerms) {
    const term = insightsTerms.find((it) => it.id === measure.insights_term_id);
    if (term) dueTermName = term.name;
  }

  return (
    <View as="div">
      <Flex alignItems="center" gap="small" wrap="wrap">
        <Flex.Item>
          <Text weight="bold">{item.item_name}</Text>
        </Flex.Item>
        <Flex.Item>
          <Pill color={statusColors[status] || "primary"}>
            {t(statusKeys[status] || statusKeys.not_started)}
          </Pill>
        </Flex.Item>
      </Flex>
      {measure.assignees && (
        <View as="div" margin="x-small 0 0 0">
          <Text size="small" color="secondary">
            {measure.assignees}
          </Text>
        </View>
      )}
      {dueTermName && (
        <View as="div" margin="x-small 0 0 0">
          <Text size="small" color="secondary">
            {t("assessmentPlanningDashboard.meetingAgenda.dueTerm", {
              term: dueTermName,
            })}
          </Text>
        </View>
      )}
    </View>
  );
};

/**
 * Fallback rendering when the source item cannot be resolved.
 */
const renderFallbackItem = (item) => {
  return (
    <View as="div">
      <Text weight="bold">{item.item_name}</Text>
    </View>
  );
};

/**
 * Lookup and render an agenda item based on its source_type.
 */
const renderAgendaItemContent = (
  item,
  assessmentMap,
  qualitativeMeasureMap,
  insightsTerms,
  canvasTerms,
  t,
  resolveTermStatuses
) => {
  if (item.source_type === "assessment") {
    let assessment = assessmentMap[item.source_id];
    if (!assessment) {
      const parts = item.source_id.split("=");
      if (parts.length > 1) {
        assessment = assessmentMap[parts[1]] || assessmentMap[parts[0]];
      }
      if (!assessment) {
        const matchKey = Object.keys(assessmentMap).find(
          (key) =>
            key === item.source_id ||
            item.source_id.includes(key) ||
            key.includes(item.source_id)
        );
        if (matchKey) assessment = assessmentMap[matchKey];
      }
    }
    if (assessment) {
      return renderAssessmentItem(
        item,
        assessment,
        insightsTerms,
        canvasTerms,
        t,
        resolveTermStatuses
      );
    }
  } else if (item.source_type === "qualitative_measure") {
    let measure = qualitativeMeasureMap[item.source_id];
    if (!measure) {
      const parts = item.source_id.split("=");
      if (parts.length > 1) {
        measure =
          qualitativeMeasureMap[parts[1]] || qualitativeMeasureMap[parts[0]];
      }
      if (!measure) {
        const matchKey = Object.keys(qualitativeMeasureMap).find(
          (key) =>
            key === item.source_id ||
            item.source_id.includes(key) ||
            key.includes(item.source_id)
        );
        if (matchKey) measure = qualitativeMeasureMap[matchKey];
      }
    }
    if (measure) {
      return renderQualitativeMeasureItem(item, measure, insightsTerms, t);
    }
  }
  return renderFallbackItem(item);
};

/**
 * AgendaItem — Renders a single agenda item card with hydrated display,
 * move/remove controls, and a per-item RichTextEditor for discussion notes.
 */
const AgendaItem = ({
  item,
  indexInGroup,
  groupItems,
  assessmentMap,
  qualitativeMeasureMap,
  insightsTerms,
  canvasTerms,
  setAgenda,
  resolveTermStatuses,
}) => {
  const { t } = useTranslation();

  const handleMoveUp = () => {
    if (indexInGroup <= 0) return;
    const prevItem = groupItems[indexInGroup - 1];
    setAgenda((prev) => {
      if (!prev) return prev;
      const items = (prev.items || []).map((it) => {
        if (it.item_id === item.item_id)
          return { ...it, sequence: prevItem.sequence };
        if (it.item_id === prevItem.item_id)
          return { ...it, sequence: item.sequence };
        return it;
      });
      return { ...prev, items };
    });
  };

  const handleMoveDown = () => {
    if (indexInGroup >= groupItems.length - 1) return;
    const nextItem = groupItems[indexInGroup + 1];
    setAgenda((prev) => {
      if (!prev) return prev;
      const items = (prev.items || []).map((it) => {
        if (it.item_id === item.item_id)
          return { ...it, sequence: nextItem.sequence };
        if (it.item_id === nextItem.item_id)
          return { ...it, sequence: item.sequence };
        return it;
      });
      return { ...prev, items };
    });
  };

  const handleRemove = () => {
    setAgenda((prev) => {
      if (!prev) return prev;
      const filtered = (prev.items || []).filter(
        (i) => i.item_id !== item.item_id
      );
      const resequenced = filtered.map((it, i) => ({ ...it, sequence: i }));
      return { ...prev, items: resequenced };
    });
  };

  const handleNotesChange = (html) => {
    setAgenda((prev) => {
      if (!prev) return prev;
      const updatedItems = (prev.items || []).map((it) =>
        it.item_id === item.item_id ? { ...it, notes: html } : it
      );
      return { ...prev, items: updatedItems };
    });
  };

  return (
    <View
      as="div"
      padding="small"
      margin="0 0 small 0"
      borderWidth="small"
      borderRadius="medium"
      role="listitem"
    >
      <Flex justifyItems="space-between" alignItems="start">
        <Flex.Item shouldGrow shouldShrink>
          {renderAgendaItemContent(
            item,
            assessmentMap,
            qualitativeMeasureMap,
            insightsTerms,
            canvasTerms,
            t,
            resolveTermStatuses
          )}
        </Flex.Item>
        <Flex.Item>
          <Flex gap="xx-small" className="no-print">
            <IconButton
              renderIcon={IconArrowOpenUpLine}
              screenReaderLabel={t(
                "assessmentPlanningDashboard.meetingAgenda.moveUp",
                { name: item.item_name }
              )}
              size="small"
              onClick={handleMoveUp}
              disabled={indexInGroup === 0}
            />
            <IconButton
              renderIcon={IconArrowOpenDownLine}
              screenReaderLabel={t(
                "assessmentPlanningDashboard.meetingAgenda.moveDown",
                { name: item.item_name }
              )}
              size="small"
              onClick={handleMoveDown}
              disabled={indexInGroup >= groupItems.length - 1}
            />
            <IconButton
              renderIcon={IconTrashLine}
              screenReaderLabel={t(
                "assessmentPlanningDashboard.meetingAgenda.removeItem",
                { name: item.item_name }
              )}
              size="small"
              color="danger"
              onClick={handleRemove}
            />
          </Flex>
        </Flex.Item>
      </Flex>
      {/* Per-item discussion notes */}
      <View as="div" margin="small 0 0 0">
        <RichTextEditor
          id={`agenda-notes-${item.item_id}`}
          label={
            <Text size="small" weight="bold">
              {t("assessmentPlanningDashboard.meetingAgenda.notesLabel")}
            </Text>
          }
          defaultValue={item.notes || ""}
          onChange={(html) => handleNotesChange(html)}
        />
      </View>
    </View>
  );
};

export default AgendaItem;
